import { v4 as uuidv4 } from 'uuid';

const createUiId = () => uuidv4();

export const createEmptyItem = () => ({
    _uiId: createUiId(),
    name: '',
    quantity: ''
});

export const createEmptyOption = (label = 'Opção 1') => ({
    _uiId: createUiId(),
    optionId: uuidv4(),
    label,
    items: [createEmptyItem()]
});

export const createEmptyMeal = () => ({
    _uiId: createUiId(),
    mealType: '',
    title: '',
    options: [createEmptyOption()]
});

export const createEmptyDay = (dayNumber) => ({
    _uiId: createUiId(),
    dayNumber,
    objective: '',
    meals: []
});

const itemToFormState = (item = {}) => ({
    _uiId: createUiId(),
    name: item.name ?? '',
    quantity: item.quantity ?? ''
});

const optionToFormState = (option = {}, index = 0) => ({
    _uiId: createUiId(),
    optionId: option.optionId || uuidv4(),
    label: `Opção ${index + 1}`,
    items: (option.items || []).map(itemToFormState)
});

const mealToFormState = (meal = {}) => ({
    _uiId: createUiId(),
    mealType: meal.mealType ?? '',
    title: meal.title ?? '',
    options: (meal.options || []).map(optionToFormState)
});

const dayToFormState = (day = {}, index = 0) => ({
    _uiId: createUiId(),
    dayNumber: Number(day.dayNumber) || index + 1,
    objective: day.objective ?? '',
    meals: (day.meals || []).map(mealToFormState)
});

export const createNutritionPlanFormState = ({
    name = '',
    description = '',
    data = null
} = {}) => ({
    title: name,
    description,
    durationDays:
        data?.durationDays === undefined || data?.durationDays === null
            ? ''
            : Number(data.durationDays),
    days: (data?.days || []).map(dayToFormState),
    foodsToAvoid: (data?.foodsToAvoid || []).map((group) => ({
        _uiId: createUiId(),
        category: group.category ?? '',
        items: (group.items || []).map((item) => ({
            _uiId: createUiId(),
            value: item
        }))
    })),
    customNotes: (data?.customNotes || []).map((note) => ({
        _uiId: createUiId(),
        value: note
    }))
});

const cleanText = (value) => String(value ?? '').trim();

export const buildNutritionPlanPayload = (formState) => ({
    name: cleanText(formState.title),
    description: cleanText(formState.description),
    data: {
        durationDays: Number(formState.durationDays),
        days: formState.days.map((day) => ({
            dayNumber: Number(day.dayNumber),
            objective: cleanText(day.objective),
            meals: day.meals.map((meal) => ({
                mealType: cleanText(meal.mealType),
                title: cleanText(meal.title),
                options: meal.options.map((option, optionIndex) => ({
                    optionId: String(option.optionId),
                    label: `Opção ${optionIndex + 1}`,
                    items: option.items.map((item) => ({
                        name: cleanText(item.name),
                        quantity: cleanText(item.quantity)
                    }))
                }))
            }))
        })),
        foodsToAvoid: formState.foodsToAvoid.map((group) => ({
            category: cleanText(group.category),
            items: group.items.map((item) => cleanText(item.value))
        })),
        customNotes: formState.customNotes.map((note) => cleanText(note.value))
    }
});

export const syncDaysWithDuration = (days, durationDays) => {
    const duration = Number(durationDays);
    const retainedDays = days
        .filter((day) => Number(day.dayNumber) <= duration)
        .sort((a, b) => Number(a.dayNumber) - Number(b.dayNumber));
    const existingNumbers = new Set(retainedDays.map((day) => Number(day.dayNumber)));

    for (let dayNumber = 1; dayNumber <= duration; dayNumber += 1) {
        if (!existingNumbers.has(dayNumber)) retainedDays.push(createEmptyDay(dayNumber));
    }

    return retainedDays.sort((a, b) => Number(a.dayNumber) - Number(b.dayNumber));
};

export const isDayEmpty = (day) =>
    !cleanText(day.objective) && (!day.meals || day.meals.length === 0);
